import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Typography } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import MarkdownMessage from "src/_modules/AiSupportChat/components/MarkdownMessage";
import TypingIndicator from "src/_modules/AiSupportChat/components/TypingIndicator";
import EscalationCard from "src/_modules/AiSupportChat/components/EscalationCard";
import CopyButton from "src/_modules/AiSupportChat/components/CopyButton";
import FeedbackButtons from "src/_modules/AiSupportChat/components/FeedbackButtons";
import DebugPanel from "src/_modules/AiSupportChat/components/DebugPanel";
import { VISUALLY_HIDDEN } from "src/_modules/AiSupportChat/constants";
import {
  MESSAGE_ROLES,
  MESSAGE_STATUS,
} from "src/_modules/AiSupportChat/core/protocol";

/** Mapit short logo (the eagle mark), served from `public/`. */
const AVATAR_SRC = "/logo192.png";
const AVATAR_SIZE = 28;

/** Status line under an answer that didn't complete (plan §3.6). */
const STATUS_LINES = Object.freeze({
  [MESSAGE_STATUS.ABORTED]: { key: "message.aborted", isError: false },
  [MESSAGE_STATUS.FAILED]: { key: "message.failed", isError: true },
  [MESSAGE_STATUS.INTERRUPTED]: { key: "message.interrupted", isError: true },
});

const BUBBLE_SX = {
  px: 1.5,
  py: 1,
  borderRadius: 2,
  overflowWrap: "anywhere",
};

const UserBubble = ({ content, label }) => {
  const { t } = useTranslation("aiSupport");
  const speaker = label === undefined ? t("message.you") : label;
  return (
    <Box
      data-ai-message="user"
      sx={{ display: "flex", justifyContent: "flex-end" }}
    >
      <Box
        sx={{
          ...BUBBLE_SX,
          maxWidth: "85%",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderEndEndRadius: "4px",
        }}
      >
        {/* Who speaks, for screen readers (answers have the bot avatar's
          alt); outside the text, so it can't sway its dir="auto" */}
        {speaker ? (
          <Box component="span" sx={VISUALLY_HIDDEN}>
            {speaker}
          </Box>
        ) : null}
        {/* What the merchant typed, as typed: never parsed as markdown */}
        <Typography
          variant="body2"
          component="p"
          dir="auto"
          sx={{ m: 0, whiteSpace: "pre-wrap" }}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

const StatusLine = ({ status, canRetry, onRetry }) => {
  const { t } = useTranslation("aiSupport");
  const line = STATUS_LINES[status];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 1,
        mt: 0.5,
        color: line.isError ? "error.main" : "text.secondary",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {line.isError && (
          <ErrorOutline sx={{ fontSize: 16 }} aria-hidden="true" />
        )}
        <Typography variant="caption">{t(line.key)}</Typography>
      </Box>
      {canRetry && (
        <Button
          size="small"
          startIcon={<Refresh />}
          onClick={onRetry}
          sx={{ textTransform: "none", py: 0 }}
        >
          {t("message.retry")}
        </Button>
      )}
    </Box>
  );
};

/**
 * One chat message (plan §3.6). Presentational only (props, no chat
 * context), so the admin Transcripts tab can reuse it (T18).
 *
 * - User: inline-end, primary background, the text exactly as typed.
 * - Assistant: inline-start with the bot avatar, markdown answer; typing
 *   dots before the first delta, a caret while streaming, and a status line
 *   for `aborted` / `failed` / `interrupted`. Retry shows on a failed or
 *   interrupted answer only when `onRetry` is passed and the error allows it.
 * - A complete answer then gets its escalation card (when the backend set
 *   one) and an actions row: Copy, and 👍 / 👎 when `onFeedbackSaved` is
 *   passed (the chat; a read-only viewer leaves it out).
 * - With `showDebug`, an answer that carries `debug` ends with the
 *   `DebugPanel` (expanded on mount with `debugDefaultExpanded`). Pass it
 *   for AI developers only: otherwise the panel isn't even mounted, whatever
 *   the server sent (non-negotiable #3).
 * - A user message starts with a visually hidden speaker label, «أنت» /
 *   "You" by default; a viewer that names the speaker itself passes
 *   `userLabel={null}` (the admin transcript: the speaker is the merchant).
 *
 * Every text block is `dir="auto"`, and long words / URLs wrap. The root
 * carries `data-ai-message` (role) and, for answers, `data-ai-status`.
 *
 * @param {object} props
 * @param {import("src/_modules/AiSupportChat/core/chatReducer").ChatMessage} props.message
 * @param {(messageId: string) => void} [props.onRetry] keep it stable (memo)
 * @param {(feedback: { messageId: string, rating: string }) => void} [props.onFeedbackSaved]
 *   keep it stable (memo)
 * @param {boolean} [props.showDebug] AI developers only
 * @param {boolean} [props.debugDefaultExpanded]
 * @param {string | null} [props.userLabel] hidden speaker label of a user
 *   message; null = none
 */
function MessageBubble({
  message,
  onRetry,
  onFeedbackSaved,
  showDebug = false,
  debugDefaultExpanded = false,
  userLabel,
}) {
  const { t } = useTranslation("aiSupport");

  if (message.role === MESSAGE_ROLES.USER) {
    return <UserBubble content={message.content} label={userLabel} />;
  }

  const { status, content } = message;
  const streaming = status === MESSAGE_STATUS.STREAMING;
  const complete = status === MESSAGE_STATUS.COMPLETE;
  const hasStatusLine = Boolean(STATUS_LINES[status]);
  const canRetry =
    Boolean(onRetry) &&
    STATUS_LINES[status]?.isError === true &&
    message.error?.retryable !== false;

  return (
    <Box
      data-ai-message="assistant"
      data-ai-status={status}
      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
    >
      <Avatar
        src={AVATAR_SRC}
        // Names the speaker for screen readers; user messages have no avatar
        alt={t("botName")}
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          flexShrink: 0,
          bgcolor: "common.white",
          border: 1,
          borderColor: "divider",
        }}
      />
      <Box sx={{ minWidth: 0, maxWidth: `calc(100% - ${AVATAR_SIZE + 8}px)` }}>
        {/* A failed answer may have no text at all: then only its status line */}
        {(content || streaming) && (
          <Box
            sx={{
              ...BUBBLE_SX,
              bgcolor: "grey.100",
              color: "text.primary",
              border: 1,
              borderColor: "divider",
              borderStartStartRadius: "4px",
            }}
          >
            {streaming && !content ? (
              <TypingIndicator />
            ) : (
              <MarkdownMessage text={content} streaming={streaming} />
            )}
          </Box>
        )}
        {hasStatusLine && (
          <StatusLine
            status={status}
            canRetry={canRetry}
            onRetry={() => onRetry(message.id)}
          />
        )}
        {complete && message.escalation && (
          <Box sx={{ mt: 1 }}>
            <EscalationCard escalation={message.escalation} />
          </Box>
        )}
        {complete && (content || onFeedbackSaved) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            {content && <CopyButton content={content} />}
            {onFeedbackSaved && (
              <FeedbackButtons
                messageId={message.id}
                rating={message.feedback?.rating ?? null}
                onSaved={onFeedbackSaved}
              />
            )}
          </Box>
        )}
        {showDebug && message.debug && (
          <DebugPanel
            debug={message.debug}
            defaultExpanded={debugDefaultExpanded}
          />
        )}
      </Box>
    </Box>
  );
}

export default memo(MessageBubble);
